import { DatePipe } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	forwardRef,
	Inject,
	Injector,
	Input,
	OnInit,
	Output,
	QueryList,
	TemplateRef,
	ViewChildren
} from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { LuIconType } from 'lu-icon';
import { BsDatepickerInlineDirective } from 'ngx-bootstrap/datepicker';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { Subscription } from 'rxjs';
import { defaultLuDatetimePickerConfig } from './lu-datetime-picker.config';
import { DateTimeTypes, LuDatetimePickerConfig, RemapDateFormats, TimeFormatType } from './lu-datetime-picker.models';
import { LuDatetimePickerService } from './lu-datetime-picker.service';
import { generateTime } from './utils/time.utils';

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: 'lu-datetime-picker',
	templateUrl: './lu-datetime-picker.component.html',
	styleUrls: ['./lu-datetime-picker.component.scss'],
	providers: [
		DatePipe,
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => LuDatetimePickerComponent),
			multi: true
		}
	]
})
export class LuDatetimePickerComponent implements OnInit, ControlValueAccessor {
	@ViewChildren(PopoverDirective) popovers: QueryList<PopoverDirective>;
	@ViewChildren(BsDatepickerInlineDirective)
	datePickers: QueryList<BsDatepickerInlineDirective>;

	@Input() options: LuDatetimePickerConfig = defaultLuDatetimePickerConfig;
	@Input() parentForm: FormGroupDirective;
	@Input() disabled: boolean;
	@Input() readonly: boolean;
	// eslint-disable-next-line @angular-eslint/no-output-on-prefix
	@Output() onDatetimePickerReady: EventEmitter<any> = new EventEmitter<any>();

	DATE_TYPES = DateTimeTypes;

	time: string[];
	datetimePrimary: Date = null;
	datetimeSecondary: Date = null;
	ngControl: NgControl;
	dateTimeFormat: string;
	observeDatePickerChange: Subscription = new Subscription();

	showTimePickerPrimaryToggle = false;
	showTimePickerSecondaryToggle = false;
	dtService: LuDatetimePickerService;
	popoverVisible: boolean = false;
	/**
	 * The type of the icon
	 */
	LuIconType = LuIconType;

	constructor(
		@Inject(Injector) private inj: Injector,
		@Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef,
		@Inject(TranslateService) private translate: TranslateService,
		@Inject(DatePipe) private datePipe: DatePipe
	) {
		this.dtService = new LuDatetimePickerService(this);
	}

	onTouch: any = () => {};

	ngOnInit(): void {
		this.options = {
			...defaultLuDatetimePickerConfig,
			...this.options
		};
		this.initializeTimePicker();
		this.onDatetimePickerReady.emit({ service: this.dtService });
	}

	initializeTimePicker(): void {
		this.dateTimeFormat = this.dtService.getDatetimeFormat();
		// @ts-ignore
		this.ngControl = this.inj.get<NgControl>(NgControl);
		this.time = generateTime(this.options.timeOptions.MINUTE_STEP);
		if (this.options.hideTimepicker) {
			this.options.timepickerColsClass = '';
			this.options.datepickerColsClass = 'sm-12';
		}
	}

	updateTime(time: string, dateType: DateTimeTypes): void {
		const timeValue = time.split(' ');
		let hours = +timeValue[0].split(':')[0];
		hours = hours === 12 && timeValue[1] === TimeFormatType.AM ? 0 : hours; // if 12 AM we want to set it to 0 in 24h format
		hours = timeValue[1] === TimeFormatType.PM && hours !== 12 ? hours + 12 : hours;

		if (dateType === this.DATE_TYPES.PRIMARY) {
			this.datetimePrimary = !!this.datetimePrimary ? this.datetimePrimary : new Date();
			this.datetimePrimary.setHours(hours);
			this.datetimePrimary.setMinutes(+timeValue[0].split(':')[1]);
			this.writeValue(this.datetimePrimary.toString());
		} else {
			this.datetimeSecondary = !!this.datetimeSecondary ? this.datetimeSecondary : this.datetimePrimary;
			this.datetimeSecondary.setHours(hours);
			this.datetimeSecondary.setMinutes(+timeValue[0].split(':')[1]);
			this.writeValue([this.datetimePrimary.toString(), this.datetimeSecondary.toString()]);
		}
		this.sendDataToParentComp();
		this.onTouch();
	}

	toggleDateTimeState($event: Event, dateType: DateTimeTypes): void {
		if (dateType === this.DATE_TYPES.PRIMARY) {
			this.showTimePickerPrimaryToggle = !this.showTimePickerPrimaryToggle;
		} else {
			this.showTimePickerSecondaryToggle = !this.showTimePickerSecondaryToggle;
		}
		$event.stopPropagation();
	}

	writeValue(newModel: string | Array<string>): void {
		if (newModel && newModel instanceof Array && this.options.dateRange) {
			this.datetimePrimary = this.formatStringToDatetime(newModel[0]);
			this.datetimeSecondary = this.formatStringToDatetime(newModel[1]);
		} else if (newModel) {
			this.datetimePrimary = this.formatStringToDatetime(newModel as string);
		} else {
			this.datetimePrimary = null;
			if (!!this.datetimeSecondary) {
				this.datetimeSecondary = null;
			}
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouch = fn;
	}

	setDateToMidnightIfNoTimePicker(date: Date): Date {
		const { hideTimepicker, dateOnly, localeDateTime } = this.options;
		if (date && hideTimepicker && !dateOnly && !localeDateTime) {
			date.setUTCHours(0, 0, 0, 0);
		}
		return date;
	}

	onDateChange(value: Date, dateType: DateTimeTypes): void {
		if (!value) {
			return;
		}

		value = this.setDateToMidnightIfNoTimePicker(value);

		if (dateType === this.DATE_TYPES.PRIMARY) {
			// no need for update if value is that one
			if (this.datetimePrimary === value) {
				return;
			}

			this.datetimePrimary = value;
		} else {
			// no need for update if value is that one
			if (this.datetimeSecondary === value) {
				return;
			}

			this.datetimeSecondary = value;
		}
		this.closePopovers();
		Promise.resolve(null).then(() => this.sendDataToParentComp());
	}

	onTimeChange(value: string, dateType: DateTimeTypes): void {
		if (dateType === this.DATE_TYPES.PRIMARY) {
			this.datetimePrimary.setTime(new Date(value).getTime());
		} else {
			this.datetimeSecondary.setTime(new Date(value).getTime());
		}

		this.closePopovers();

		this.sendDataToParentComp();
		this.onTouch();
	}

	closePopovers(): void {
		this.popovers.forEach((popover: PopoverDirective) => {
			popover.hide();
		});
	}

	onInputChange($event: Event, dateType: DateTimeTypes): void {
		const { required } = this.options;
		let inputValue = ($event.target as HTMLInputElement).value;
		inputValue = this.remapInputDataToRightFormat(inputValue);
		const validManuallyInputedDate = this.isStringValidDate(inputValue);
		if (dateType === this.DATE_TYPES.PRIMARY) {
			this.datetimePrimary = this.formatStringToDatetime(inputValue);
		} else {
			this.datetimeSecondary = this.formatStringToDatetime(inputValue);
		}

		this.sendDataToParentComp();
		if (!required && inputValue === '') {
			return;
		}
		if (!validManuallyInputedDate) {
			this.ngControl.control.setErrors({ invalidDate: true });
		}
	}

	isValid(): boolean {
		let valid = true;
		if (this.ngControl.touched || (this.parentForm && this.parentForm.submitted)) {
			valid = this.ngControl.valid;
		}
		return valid;
	}

	/**
	 * On date picker shown
	 */
	onDateShown(datePickerContent: TemplateRef<any>): void {
		setTimeout(() => {
			const nativeElement = datePickerContent.elementRef.nativeElement.parentElement;
			this.observeDatePickerChange.add(
				(this.datePickers.first as any)._datepicker.instance._store.source.subscribe(() => {
					this.a11yForDatePicker(nativeElement);
				})
			);
		});
	}

	/**
	 * On date picker hidden
	 */
	onDateHidden(): void {
		this.observeDatePickerChange.unsubscribe();
	}

	/**
	 * Set role button to all of the spans and aria-disabled if it contains class disabled
	 * Set title to next and previous month buttons
	 * @param nativeElement
	 * @private
	 */
	private a11yForDatePicker(nativeElement: any): void {
		setTimeout(() => {
			const datePickerHeader = nativeElement.querySelector('.bs-datepicker-head');
			const datePickerBody = nativeElement.querySelector('.bs-datepicker-body');
			const previousButton = datePickerHeader.querySelector('.previous');
			const nextButton = datePickerHeader.querySelector('.next');
			const availableDates = datePickerBody.querySelectorAll('tbody td');

			previousButton.setAttribute('title', this.translate.instant('lup.a11y.calendar_previous'));
			nextButton.setAttribute('title', this.translate.instant('lup.a11y.calendar_next'));

			for (const item of availableDates) {
				const span = item.querySelector('span');
				span.setAttribute('role', 'button');
				span.setAttribute('tabindex', 0);
				if (item instanceof HTMLElement && item.classList.contains('disabled')) {
					span.setAttribute('aria-disabled', true);
				}
			}
		});
	}

	private onChange: any = () => {};

	private sendDataToParentComp(): void {
		const { localeDateTime, dateOnly, required, dateRange } = this.options;
		let primaryDate: string;
		if (!required && !this.datetimePrimary) {
			this.onChange(null);
			return;
		}
		if (localeDateTime) {
			primaryDate = this.datetimePrimary ? this.datetimePrimary.toString() : null;
		} else if (dateOnly) {
			// https://github.com/valor-software/ngx-bootstrap/issues/3609
			const getDateInUnifiedFormat = new Date(
				this.datetimePrimary.getTime() - this.datetimePrimary.getTimezoneOffset() * 60 * 1000
			);
			primaryDate = this.datePipe.transform(getDateInUnifiedFormat, 'yyyy-MM-dd', 'UTC');
		} else {
			primaryDate = !!this.datetimePrimary ? this.datetimePrimary.toISOString() : null;
		}

		if (dateRange) {
			let secondaryDate: string;
			if (localeDateTime) {
				secondaryDate = this.datetimeSecondary ? this.datetimeSecondary.toString() : null;
			} else if (dateOnly) {
				// https://github.com/valor-software/ngx-bootstrap/issues/3609
				const getDateInUnifiedFormat = new Date(
					this.datetimeSecondary.getTime() - this.datetimeSecondary.getTimezoneOffset() * 60 * 1000
				);
				secondaryDate = this.datePipe.transform(getDateInUnifiedFormat, 'yyyy-MM-dd', 'UTC');
			} else {
				secondaryDate = !!this.datetimeSecondary ? this.datetimeSecondary.toISOString() : null;
			}

			this.onChange([primaryDate, secondaryDate]);

			if (this.ngControl.touched && !this.isValidRange()) {
				this.ngControl.control.setErrors({ invalidRange: true });
				return;
			}

			if (required && this.ngControl.touched && this.options.primaryRequired && !this.ngControl.control.value[0]) {
				this.ngControl.control.setErrors({
					primaryRequiredMessage: this.translate.instant(this.options.primaryRequiredMessage)
				});
				return;
			}

			if (required && this.ngControl.touched && this.options.secondaryRequired && !this.ngControl.control.value[1]) {
				this.ngControl.control.setErrors({
					secondaryRequiredMessage: this.translate.instant(this.options.secondaryRequiredMessage)
				});
				return;
			}
		} else {
			this.onChange(primaryDate);
		}
	}

	private isValidRange(): boolean {
		if (!!this.datetimePrimary && !!this.datetimeSecondary) {
			return this.datetimePrimary.getTime() <= this.datetimeSecondary.getTime();
		}
		return true;
	}

	private isStringValidDate(dateString: string): boolean {
		const date = new Date(dateString);
		const isValidDate = !isNaN(date.valueOf());
		return !(!dateString || !isValidDate);
	}

	private shouldOnsetTimezone(date: string | Date): boolean {
		if (typeof date === 'object') {
			return !!this.options.hideTimepicker;
		}

		// the date string does not includes a 'T' (not in ISO format)
		// the date string includes a '-' (indicating it's a date format)
		return !!this.options.hideTimepicker && !date.includes('T') && date.includes('-');
	}

	private formatStringToDatetime(date: string | Date): Date | null {
		// Ensure the date is valid
		if (!date) {
			return null;
		}

		let formattedDate: Date = new Date(date);

		// If shouldOnsetTimezone returns true, modify the date to avoid timezone offset issues
		if (this.shouldOnsetTimezone(date)) {
			if (typeof date === 'object') {
				// If the input was an object, reformat it to 'YYYY-MM-DDT00:00:00'
				formattedDate = new Date(this.formatDateToYYYYMMDD(date) + 'T00:00:00');
			} else {
				// If the input was a string, append 'T00:00:00' to the string
				formattedDate = new Date(date + 'T00:00:00');
			}
		}

		// Ensure the date is valid
		if (isNaN(formattedDate.valueOf())) {
			return null;
		}

		return formattedDate;
	}

	/**
	 * Format date to YYYY-MM-DD string format to reset time to 00:00:00
	 * @param date - Date object to format
	 * @returns {string} - Formatted date string
	 */
	formatDateToYYYYMMDD(date: Date): string {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');

		return `${year}-${month}-${day}`;
	}

	/***
	 * We support multiple date formats and on input change we want to allow user to actually have day(- or /)month(- or /)year
	 */
	private remapInputDataToRightFormat(dateString: string): string {
		let splittedDate: string[];
		const { dateFormat, required } = this.options;
		switch (dateFormat) {
			// remapping formats that Javascript Date object doesn't support (day before month)
			case RemapDateFormats.DAY_MONTH_YEAR:
				if (!required && dateString === '') {
					return dateString;
				}
				splittedDate = dateString.split('/');
				return `${splittedDate[1]}/${splittedDate[0]}/${splittedDate[2]}`;

			case RemapDateFormats.DAY_MONTH_YEAR_DASHED:
				if (!required && dateString === '') {
					return dateString;
				}
				splittedDate = dateString.split('-');
				return `${splittedDate[1]}/${splittedDate[0]}/${splittedDate[2]}`;

			default:
				return dateString;
		}
	}

	clearSelection(event: Event, dateInput: HTMLInputElement): void {
		// Prevent page scroll and popover opening
		event.preventDefault();
		event.stopPropagation();

		this.writeValue(null);
		this.sendDataToParentComp();
		// We have to manually clear the input value, as the writeValue method does not do it if the input is invalid
		dateInput.value = '';
	}
}
